<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    <h1 class="text-2xl font-semibold text-gray-900">Mon compte</h1>
    
    <div v-if="loading" class="mt-6">
      <div class="animate-spin rounded-full h-12 w-12 border-4 border-zebu-orange border-b-transparent"></div>
      <p class="mt-4 text-gray-600">Chargement...</p>
    </div>

    <div v-else class="mt-6 space-y-6">
      <!-- Informations de base -->
      <div class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 class="text-lg leading-6 font-medium text-gray-900">Informations personnelles</h3>
            <p class="mt-1 max-w-2xl text-sm text-gray-500">Gérez vos informations personnelles</p>
          </div>
          <button
            @click="isEditing = !isEditing"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-zebu-orange hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
          >
            {{ isEditing ? 'Annuler' : 'Modifier' }}
          </button>
        </div>

        <!-- Mode lecture -->
        <div v-if="!isEditing" class="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">Prénom</dt>
              <dd class="mt-1 text-sm text-gray-900">{{ user.firstname }}</dd>
            </div>
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">Nom</dt>
              <dd class="mt-1 text-sm text-gray-900">{{ user.lastname }}</dd>
            </div>
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">Nom d'utilisateur</dt>
              <dd class="mt-1 text-sm text-gray-900">{{ user.username }}</dd>
            </div>
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">Email</dt>
              <dd class="mt-1 text-sm text-gray-900">{{ user.email }}</dd>
            </div>
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">Organisation</dt>
              <dd class="mt-1 text-sm text-gray-900">{{ user.organization || 'Non renseigné' }}</dd>
            </div>
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">Téléphone</dt>
              <dd class="mt-1 text-sm text-gray-900">{{ user.phone_number || 'Non renseigné' }}</dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">Adresse</dt>
              <dd class="mt-1 text-sm text-gray-900">
                {{ user.address }}<br v-if="user.additional_address">
                {{ user.additional_address }}<br v-if="user.additional_address">
                {{ user.zip_code }} {{ user.city }}<br>
                {{ user.country?.name || 'Pays non renseigné' }}
              </dd>
            </div>

            <!-- Informations d'adhésion -->
            <div class="sm:col-span-2 border-t border-gray-200 pt-4 mt-4">
              <dt class="text-sm font-medium text-gray-500">Informations d'adhésion</dt>
              <div class="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt class="text-xs text-gray-500">Adhésion valable jusqu'au</dt>
                  <dd 
                    :class="[
                      'text-sm',
                      isSubscriptionExpired ? 'text-red-600 font-medium' : 'text-green-600 font-medium'
                    ]"
                  >
                    {{ formatSubscriptionEndDate }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Statut de l'adhésion</dt>
                  <dd class="text-sm">
                    <span 
                      :class="[
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        user.subscription?.is_active ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      ]"
                    >
                      {{ user.status }}
                    </span>
                  </dd>
                </div>
              </div>
            </div>
          </dl>
        </div>

        <!-- Mode édition -->
        <form v-else @submit.prevent="handleSubmit" class="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
            <!-- Prénom -->
            <div>
              <label for="firstname" class="block text-sm font-medium text-gray-700">Prénom *</label>
              <input
                type="text"
                id="firstname"
                v-model="formData.firstname"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Nom -->
            <div>
              <label for="lastname" class="block text-sm font-medium text-gray-700">Nom *</label>
              <input
                type="text"
                id="lastname"
                v-model="formData.lastname"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Nom d'utilisateur -->
            <div>
              <label for="username" class="block text-sm font-medium text-gray-700">Nom d'utilisateur *</label>
              <input
                type="text"
                id="username"
                v-model="formData.username"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700">Email *</label>
              <input
                type="email"
                id="email"
                v-model="formData.email"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Organisation -->
            <div>
              <label for="organization" class="block text-sm font-medium text-gray-700">Organisation</label>
              <input
                type="text"
                id="organization"
                v-model="formData.organization"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Téléphone -->
            <div>
              <label for="phone_number" class="block text-sm font-medium text-gray-700">Téléphone</label>
              <input
                type="tel"
                id="phone_number"
                v-model="formData.phone_number"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Téléphone additionnel -->
            <div>
              <label for="additional_phone_number" class="block text-sm font-medium text-gray-700">Téléphone secondaire</label>
              <input
                type="tel"
                id="additional_phone_number"
                v-model="formData.additional_phone_number"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Fax -->
            <div>
              <label for="fax_number" class="block text-sm font-medium text-gray-700">Fax</label>
              <input
                type="tel"
                id="fax_number"
                v-model="formData.fax_number"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Adresse -->
            <div class="sm:col-span-2">
              <label for="address" class="block text-sm font-medium text-gray-700">Adresse *</label>
              <input
                type="text"
                id="address"
                v-model="formData.address"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Complément d'adresse -->
            <div class="sm:col-span-2">
              <label for="additional_address" class="block text-sm font-medium text-gray-700">Complément d'adresse</label>
              <input
                type="text"
                id="additional_address"
                v-model="formData.additional_address"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Code postal -->
            <div>
              <label for="zip_code" class="block text-sm font-medium text-gray-700">Code postal *</label>
              <input
                type="text"
                id="zip_code"
                v-model="formData.zip_code"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Ville -->
            <div>
              <label for="city" class="block text-sm font-medium text-gray-700">Ville *</label>
              <input
                type="text"
                id="city"
                v-model="formData.city"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              />
            </div>

            <!-- Pays -->
            <div class="sm:col-span-2">
              <label for="country_id" class="block text-sm font-medium text-gray-700">Pays *</label>
              <select
                id="country_id"
                v-model="formData.country_id"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-zebu-orange focus:ring-zebu-orange sm:text-sm"
              >
                <option v-for="country in countries" :key="country.id" :value="country.id">
                  {{ country.name }}
                </option>
              </select>
            </div>

            <!-- Newsletter -->
            <div class="sm:col-span-2">
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="has_newsletter"
                  v-model="formData.has_newsletter"
                  class="h-4 w-4 text-zebu-orange focus:ring-zebu-orange border-gray-300 rounded"
                />
                <label for="has_newsletter" class="ml-2 block text-sm text-gray-700">
                  Je souhaite recevoir la newsletter
                </label>
              </div>
            </div>
          </div>

          <div class="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              @click="isEditing = false"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-zebu-orange hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange disabled:opacity-50"
            >
              <svg
                v-if="isSubmitting"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Section Réadhésion -->
      <div v-if="shouldShowRenewal" class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900">Réadhésion</h3>
          <p class="mt-1 max-w-2xl text-sm text-gray-500">
            Votre adhésion arrive bientôt à échéance. Renouvelez-la dès maintenant !
          </p>
        </div>
        <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div class="text-center">
            <p class="text-3xl font-bold text-gray-900">13,60 €</p>
            <p class="mt-1 text-sm text-gray-500">après déduction fiscale de 66%</p>
            <p class="mt-2 text-sm text-gray-500">(cotisation annuelle de 40 €)</p>
            <button
              @click="handleRenewal"
              :disabled="isProcessingPayment"
              class="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-zebu-orange hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange disabled:opacity-50"
            >
              <svg
                v-if="isProcessingPayment"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ isProcessingPayment ? 'Traitement en cours...' : 'Renouveler mon adhésion' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { memberareaService } from '@/services/memberarea.service'
import { useToast } from 'vue-toastification'

const toast = useToast()
const loading = ref(true)
const isEditing = ref(false)
const isSubmitting = ref(false)
const isProcessingPayment = ref(false)
const user = ref({})
const countries = ref([])
const dashboardData = ref({})
const formData = ref({
  firstname: '',
  lastname: '',
  username: '',
  email: '',
  organization: '',
  address: '',
  additional_address: '',
  zip_code: '',
  city: '',
  phone_number: '',
  additional_phone_number: '',
  fax_number: '',
  has_newsletter: false,
  country_id: null
})

const formatDate = (date) => {
  if (!date) return 'Non disponible'
  return new Date(date).toLocaleDateString('fr-FR')
}

const shouldShowRenewal = computed(() => {
  if (!user.value.subscription_ended_at) return false
  const endDate = new Date(user.value.subscription_ended_at)
  const today = new Date()
  const diffTime = endDate - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays <= 30
})

const getCountryName = (countryId) => {
  const country = countries.value.find(c => c.id === countryId)
  return country ? country.name : 'Pays non renseigné'
}

const initFormData = () => {
  Object.keys(formData.value).forEach(key => {
    formData.value[key] = user.value[key] ?? formData.value[key]
  })
}

const handleSubmit = async () => {
  isSubmitting.value = true
  try {
    // Vérifier si l'ID utilisateur existe
    if (!user.value?.id) {
      throw new Error('ID utilisateur non trouvé')
    }

    const payload = {
      ...formData.value,
      userId: user.value.id
    }

    const response = await memberareaService.updateProfile(payload)
    if (response.success) {
      user.value = response.user
      isEditing.value = false
      toast.success('Profil mis à jour avec succès')
    } else {
      // Gérer les erreurs de réponse avec success: false
      throw new Error(response.message || 'Erreur lors de la mise à jour du profil')
    }
  } catch (error) {
    console.error('Erreur détaillée:', error)
    // Afficher le message d'erreur complet
    const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la mise à jour du profil'
    const errorDetail = error.response?.data?.error ? `\nDétail: ${error.response.data.error}` : ''
    toast.error(`${errorMessage}${errorDetail}`, {
      timeout: 5000, // Augmenter le temps d'affichage pour les erreurs
      closeButton: true, // Ajouter un bouton de fermeture
      position: "bottom-center" // Position plus visible
    })
  } finally {
    isSubmitting.value = false
  }
}

const handleRenewal = async () => {
  if (isProcessingPayment.value) return
  
  isProcessingPayment.value = true
  try {
    const response = await memberareaService.createMembershipPayment()
    if (response.success && response.url) {
      window.location.href = response.url
    } else {
      toast.error("Une erreur est survenue lors de l'initialisation du paiement")
    }
  } catch (error) {
    toast.error(error.response?.data?.message || "Une erreur est survenue lors de l'initialisation du paiement")
  } finally {
    isProcessingPayment.value = false
  }
}

const isSubscriptionExpired = computed(() => {
  if (!dashboardData.value?.subscription_end_date) return true;
  return new Date(dashboardData.value.subscription_end_date) < new Date();
});

const formatSubscriptionEndDate = computed(() => {
  if (!dashboardData.value?.subscription_end_date) return 'Non disponible';
  return formatDate(dashboardData.value.subscription_end_date);
});

onMounted(async () => {
  try {
    loading.value = true
    const [userData, countriesData, dashboardResponse] = await Promise.all([
      memberareaService.getProfileDetails(),
      memberareaService.getCountries(),
      memberareaService.getDashboardData()
    ])
    
    if (userData.success) {
      user.value = userData.user
      user.value.country_id = user.value.country?.id
    }
    
    if (dashboardResponse.success) {
      dashboardData.value = dashboardResponse
      // Mettre à jour le statut de l'utilisateur avec celui du dashboard
      user.value.status = dashboardResponse.user.status
      user.value.subscription = dashboardResponse.subscription_status
    }
    
    countries.value = countriesData
    initFormData()
  } catch (error) {
    toast.error('Erreur lors du chargement des données')
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
})
</script> 